import React, { useEffect, useRef, useState } from 'react';
import { SortDown, SortUp } from 'react-bootstrap-icons';
import { Button, Typography } from '@ghq-abi/design-system-v2';

import { cn } from '~/lib/utils';

import { SEARCH_BAR_STYLES } from '../constants';

export interface SortOption {
  id: string;
  label: string;
  icon?: React.ReactNode;
  value?: string | number;
  direction?: 'asc' | 'desc';
}

export interface SortButtonProps {
  text?: string;
  disabled?: boolean;
  className?: string;
  onSortSelect?: (option: {
    id: string;
    label: string;
    value?: string | number;
    direction?: string;
  }) => void;
  selectedSortValue?: string | number | null;
  options?: SortOption[];
}

const DEFAULT_SORT_OPTIONS: SortOption[] = [
  {
    id: 'latest',
    label: 'Latest Request',
    icon: React.createElement(SortUp, { className: 'w-4 h-4' }),
    value: 'createdAt_desc',
    direction: 'desc'
  },
  {
    id: 'oldest',
    label: 'Oldest Request',
    icon: React.createElement(SortDown, { className: 'w-4 h-4' }),
    value: 'createdAt_asc',
    direction: 'asc'
  }
];

export const SortButton: React.FC<SortButtonProps> = ({
  text = 'Sort by',
  disabled = false,
  className,
  onSortSelect,
  selectedSortValue,
  options = DEFAULT_SORT_OPTIONS
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<SortOption | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Encontrar opção selecionada baseada no valor
  useEffect(() => {
    if (selectedSortValue !== undefined) {
      const option = options.find(opt => opt.value === selectedSortValue);
      setSelectedOption(option || null);
    }
  }, [selectedSortValue, options]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleOptionClick = (option: SortOption) => {
    setSelectedOption(option);
    setIsOpen(false);
    if (onSortSelect) {
      onSortSelect({
        id: option.id,
        label: option.label,
        value: option.value,
        direction: option.direction
      });
    }
  };

  return (
    <div
      ref={dropdownRef}
      className={cn(SEARCH_BAR_STYLES.BUTTON_CONTAINER, className, 'relative')}>
      <Button
        variant="secondary"
        size="md"
        border="default"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className="flex items-center">
        <div className="flex-shrink-0 mr-2">
          {isOpen ? (
            <SortUp className="w-4 h-4" />
          ) : (
            <SortDown className="w-4 h-4" />
          )}
        </div>
        <span className="text-sm">{text}</span>
      </Button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full mt-1 right-0 w-full min-w-[200px] bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="py-1">
            {options.length > 0 ? (
              options.map(option => (
                <button
                  key={option.id}
                  onClick={() => handleOptionClick(option)}
                  className={cn(
                    'w-full px-4 py-2 text-left hover:bg-gray-50 transition-colors duration-200 flex items-center gap-3',
                    selectedOption?.id === option.id &&
                      'bg-blue-50 text-blue-700'
                  )}>
                  {/* Ícone da opção */}
                  {option.icon && (
                    <div className="flex-shrink-0">{option.icon}</div>
                  )}

                  {/* Texto da opção */}
                  <Typography
                    variant="body-sm-medium"
                    color="default"
                    className={
                      selectedOption?.id === option.id ? 'text-blue-700' : ''
                    }>
                    {option.label}
                  </Typography>
                </button>
              ))
            ) : (
              <div className="px-4 py-2 text-gray-500">
                <Typography variant="body-sm-medium" color="light">
                  No options available
                </Typography>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
